import {
  faDatabase, faDownload, faTableCells,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Label,
} from 'reactstrap';
import styled from 'styled-components';

import {
  DATA_PACK, DIRECTIONS, DOWNLOAD_SELECTOR, METER, TRADE,
} from 'src/util/constants';

const DownloadWrapper = styled(DropdownToggle)`
{
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
`;

/**
 * File download dropdown
 * @param {object} props - Component props.
 * @param {Function} props.downloadFunc - Function to handle the file download.
 * @param {object} props.mainData - mainData for preparing the downloadable file
 * @param {boolean} props.downloadSelectorOpen - Controls whether the dropdown is open.
 * @param {Function} props.toggleFunc - Function to toggle the dropdown.
 * @param {object} props.dateRange - Selected date range for the download.
 * @returns {React.ReactElement} The download dropdown menu.
 */
function FileDownloadSelector({
  downloadFunc = null,
  mainData,
  downloadSelectorOpen = false,
  toggleFunc,
  dateRange,
}) {
  const meterData = [];
  const tradeData = [];

  // intl messages
  const intl = useIntl();
  const meterDataDownloadLabel = intl.formatMessage({ id: 'dashboard.file_download_selector.download.csv.meter_data', defaultMessage: 'Meter data (.csv)' });
  const tradeDataDownloadLabel = intl.formatMessage({ id: 'dashboard.file_download_selector.download.csv.trade_data', defaultMessage: 'Trade data (.csv)' });
  const jsonDataDownloadLabel = intl.formatMessage({ id: 'dashboard.file_download_selector.download.json', defaultMessage: 'Data pack (.json)' });

  DIRECTIONS.forEach((dir) => {
    if (!mainData[dir]?.data) return;
    Object.keys(mainData[dir].data)?.forEach((timestamp) => {
      const { meterDataAggregates, tradeSetSummaries } = mainData[dir].data[timestamp] || {};
      if (meterDataAggregates) {
        Object.keys(meterDataAggregates).forEach(
          (meterId) => meterData.push(meterDataAggregates[meterId]),
        );
      }
      if (tradeSetSummaries) {
        Object.keys(tradeSetSummaries).forEach(
          (tradeRuleId) => tradeData.push(tradeSetSummaries[tradeRuleId]),
        );
      }
    });
  });

  let meterDataCSVDisabled = false;
  let tradeDataCSVDisabled = false;
  let dataPackJSONDisabled = false;

  if (meterData.length === 0 && tradeData.length === 0) {
    meterDataCSVDisabled = true;
    tradeDataCSVDisabled = true;
    dataPackJSONDisabled = true;
  } else {
    meterDataCSVDisabled = meterData.length === 0;
    tradeDataCSVDisabled = tradeData.length === 0;
  }

  return (
    <Dropdown className="download-type-selector" isOpen={downloadSelectorOpen} toggle={() => toggleFunc(DOWNLOAD_SELECTOR)}>
      <DownloadWrapper className="btn btn-darken" caret>
        <FontAwesomeIcon icon={faDownload} size="1x" className="me-2" />
      </DownloadWrapper>
      <DropdownMenu end>
        <DropdownItem
          disabled={meterDataCSVDisabled}
          className={`${meterDataCSVDisabled ? 'pe-none' : 'pe-auto'}`}
          onClick={() => downloadFunc(METER, mainData, dateRange, intl)}
          // eslint-disable-next-line jsx-a11y/aria-proptypes
          aria-disabled={`${!!meterDataCSVDisabled}`}
          data-testid="meter-data-csv"
        >
          <Label>
            <FontAwesomeIcon icon={faTableCells} size="1x" className="me-2" />
            {meterDataDownloadLabel}
          </Label>
        </DropdownItem>
        <DropdownItem
          disabled={tradeDataCSVDisabled}
          className={`${tradeDataCSVDisabled ? 'pe-none' : 'pe-auto'}`}
          onClick={() => downloadFunc(TRADE, mainData, dateRange, intl)}
          // eslint-disable-next-line jsx-a11y/aria-proptypes
          aria-disabled={`${!!tradeDataCSVDisabled}`}
          data-testid="trade-data-csv"
        >
          <Label>
            <FontAwesomeIcon icon={faTableCells} size="1x" className="me-2" />
            {tradeDataDownloadLabel}
          </Label>
        </DropdownItem>
        <DropdownItem
          disabled={dataPackJSONDisabled}
          className={`${dataPackJSONDisabled ? 'pe-none' : 'pe-auto'}`}
          onClick={() => downloadFunc(
            DATA_PACK,
            mainData,
            dateRange,
            intl,
          )}
          // eslint-disable-next-line jsx-a11y/aria-proptypes
          aria-disabled={`${!!dataPackJSONDisabled}`}
          data-testid="data-pack-json"
        >
          <Label>
            <FontAwesomeIcon icon={faDatabase} size="1x" className="me-2" />
            {jsonDataDownloadLabel}
          </Label>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

export default FileDownloadSelector;

FileDownloadSelector.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  dateRange: PropTypes.object.isRequired,
  downloadFunc: PropTypes.func,
  downloadSelectorOpen: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  mainData: PropTypes.object.isRequired,
  toggleFunc: PropTypes.func.isRequired,

};
